import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import axios from 'axios';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import PaginaSuscripcion from '../components/suscripcion/PaginaSuscripcion';
import BarraDePasos from '../components/suscripcion/BarraDePasos';
import TarjetaAU from '../components/suscripcion/TarjetaAU';
import EtiquetaAU from '../components/suscripcion/EtiquetaAU';
import BotonAU from '../components/suscripcion/BotonAU';
import OpcionDePago from '../components/suscripcion/OpcionDePago';
import SelloFlow from '../components/suscripcion/SelloFlow';
import ErrorConCodigo from '../components/ErrorConCodigo';
import usePlan from '../hooks/usePlan';
import { formatFechaConOffset } from '../utils/fechaSuscripcion';
import { formatoCLP } from '../utils/montoCLP';
import { irA } from '../utils/navegar';
import { AU, BORDE_AZUL, CONTORNO_FOCO, FUENTE } from '../styles/tokensAU';

const MENSAJE_SIN_COMPLETAR = 'No pudimos completar tu solicitud. Intenta de nuevo o escríbele a Agustín por WhatsApp.';
const MENSAJE_SIN_CONEXION = 'No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.';

// El servidor respondió, pero con algo que la página no sabe usar. Es una marca propia (no se
// compara texto) para no confundirlo con una caída de red: ahí sí que no hubo respuesta.
class RespuestaInesperada extends Error {}

const Fila = ({ etiqueta, valor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
    <EtiquetaAU>{etiqueta}</EtiquetaAU>
    <Box component="span" sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 600, color: AU.oscuro, textAlign: 'right', overflowWrap: 'anywhere' }}>
      {valor}
    </Box>
  </Box>
);

// Paso 2 de 2: cómo pagar. Recibe nombre y correo por el estado del navegador (nunca por la URL).
const SubscriptionPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = usePlan(location.search);
  const persona = location.state || {};
  // El pago automático viene marcado: es lo recomendado y la persona lo cambia a mano si quiere.
  const [metodo, setMetodo] = useState('automatico');
  const [enviando, setEnviando] = useState(false);
  // El estado no alcanza contra un doble clic: dos clics seguidos pueden llegar antes de que el botón se bloquee.
  const enviandoRef = useRef(false);
  const [error, setError] = useState('');
  const [errorCodigo, setErrorCodigo] = useState('');

  // Al volver atrás desde Flow el navegador puede restaurar esta página tal cual estaba (bfcache), con el
  // botón todavía en "cargando". `persisted` distingue esa restauración de una carga normal.
  useEffect(() => {
    const alMostrarse = (evento) => { if (evento.persisted) { enviandoRef.current = false; setEnviando(false); } };
    window.addEventListener('pageshow', alMostrarse);
    return () => window.removeEventListener('pageshow', alMostrarse);
  }, []);

  // Sin los datos del paso 1 (enlace copiado, pestaña restaurada) o sin plan: se vuelve al paso 1.
  if (!persona.name || !persona.email || plan.faltaPlan) {
    return <Navigate to={`/subscribe${location.search}`} replace />;
  }

  const automatico = metodo === 'automatico';
  const fechaInicio = formatFechaConOffset(plan.inicio);
  // La frase del cobro tiene que leerse bien también cuando falta el monto (cargando o con error).
  const textoMonto = plan.monto ? formatoCLP(plan.monto) : 'el monto del plan';
  const verboCobro = plan.monto ? 'Se cobran' : 'Se cobra';
  const montoResumen = (() => {
    if (plan.cargando) return 'Cargando…';
    return plan.monto ? formatoCLP(plan.monto) : 'No disponible';
  })();

  // Un error de un método (por ejemplo el 409 del pago manual) no tiene sentido con el otro.
  const cambiarMetodo = (valor) => {
    setMetodo(valor);
    setError('');
    setErrorCodigo('');
  };

  const editarDatos = () => navigate(`/subscribe${location.search}`, {
    state: { name: persona.name, email: persona.email },
  });

  const enviar = async () => {
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError('');
    setErrorCodigo('');

    // Mientras la persona sale hacia Flow el botón sigue bloqueado: evita un segundo clic.
    let seguirBloqueado = false;
    const cuerpo = { name: persona.name, email: persona.email, planId: plan.planId, subscription_start: plan.inicio };
    try {
      const API_URL = process.env.REACT_APP_API_URL;

      if (automatico) {
        const { data } = await axios.post(`${API_URL}/api/subscription/start-enrollment`, cuerpo);
        if (data?.siguiente === 'tarjeta' && data.redirectUrl) {
          seguirBloqueado = true;
          irA(data.redirectUrl);
          return;
        }
        throw new RespuestaInesperada('Respuesta inesperada del servidor');
      }

      await axios.post(`${API_URL}/api/subscription/create`, cuerpo);
      navigate('/success-request', { state: { email: persona.email, paymentOrSubscription: 'suscripción' } });
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.message
        || (err.response || err instanceof RespuestaInesperada ? MENSAJE_SIN_COMPLETAR : MENSAJE_SIN_CONEXION)
      );
      setErrorCodigo(data?.code || '');
      // Solo estado y código: el error completo de axios trae nombre y correo en `config.data`.
      console.error('Error al crear la suscripción:', err.response?.status, data?.code || err.message);
    } finally {
      if (!seguirBloqueado) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <PaginaSuscripcion>
      <BarraDePasos paso={2} />

      <Box>
        <Box
          component="button"
          type="button"
          onClick={editarDatos}
          disabled={enviando}
          sx={{
            p: 0,
            mb: 1,
            border: 0,
            bgcolor: 'transparent',
            cursor: 'pointer',
            fontFamily: FUENTE,
            fontSize: 14,
            fontWeight: 600,
            color: AU.azulHover,
            '&:disabled': { opacity: 0.6, cursor: 'default' },
            '&:focus-visible': { ...CONTORNO_FOCO },
          }}
        >
          <span aria-hidden="true">← </span>Editar mis datos
        </Box>
        <Box component="h1" sx={{ m: 0, fontFamily: FUENTE, fontSize: 26, fontWeight: 800, lineHeight: 1.15, color: AU.oscuro, textAlign: 'center' }}>
          ¿Cómo quieres pagar?
        </Box>
      </Box>

      <TarjetaAU
        aria-label="Resumen de tu suscripción"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, '& > * + *': { borderTop: `1px solid ${BORDE_AZUL}`, pt: 1.25 } }}
      >
        <Fila etiqueta="Nombre" valor={persona.name} />
        <Fila etiqueta="Correo" valor={persona.email} />
        <Fila etiqueta="Monto" valor={montoResumen} />
        <Fila etiqueta="Inicio" valor={fechaInicio} />
      </TarjetaAU>

      <Box role="radiogroup" aria-label="Método de pago" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <OpcionDePago
          valor="automatico"
          seleccionado={automatico}
          onSeleccionar={cambiarMetodo}
          titulo="Pago automático"
          detalle="Tu tarjeta se cobra sola cada 30 días. No tienes que acordarte de pagar ni esperar un link."
          recomendado
        />
        <OpcionDePago
          valor="manual"
          seleccionado={!automatico}
          onSeleccionar={cambiarMetodo}
          titulo="Pago manual"
          detalle="Cada mes te llega un correo con el link para que pagues tú."
        />
      </Box>

      {automatico && (
        <TarjetaAU>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LockOutlinedIcon aria-hidden="true" sx={{ color: AU.azulHover, fontSize: 20 }} />
            <EtiquetaAU component="h2" sx={{ m: 0, color: AU.oscuro }}>Cómo funciona el cobro</EtiquetaAU>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
            <Box component="span" sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 600, color: AU.oscuro }}>Pagos procesados por</Box>
            <SelloFlow />
          </Box>
          {/* Checks azules en vez de viñetas; role="list" porque Safari pierde la semántica con list-style: none. */}
          <Box
            component="ul"
            role="list"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              fontFamily: FUENTE,
              fontSize: 14,
              lineHeight: 1.45,
              color: AU.oscuro,
              '& > li': { display: 'flex', gap: 1.25, alignItems: 'flex-start' },
              '& > li > svg': { flexShrink: 0, mt: '1px', fontSize: 18, color: AU.blanco, bgcolor: AU.azul, borderRadius: '50%', p: '2px' },
            }}
          >
            <li>
              <CheckRoundedIcon aria-hidden="true" />
              <span>
                Ingresas tu tarjeta en el sitio de <strong>Flow</strong>, el procesador de pagos de Chile.{' '}
                <strong>AU Coaching nunca ve ni guarda el número, el vencimiento ni el código de seguridad de tu tarjeta.</strong>
              </span>
            </li>
            <li>
              <CheckRoundedIcon aria-hidden="true" />
              <span>
                {verboCobro} <strong>{textoMonto}</strong> el <strong>{fechaInicio}</strong> y luego cada 30 días.
              </span>
            </li>
            <li>
              <CheckRoundedIcon aria-hidden="true" />
              <span>
                <strong>Cancelas cuando quieras</strong>: escríbenos por WhatsApp antes de tu próxima fecha de cobro y no se te vuelve a cobrar.
              </span>
            </li>
            <li>
              <CheckRoundedIcon aria-hidden="true" />
              <span>
                <strong>Si hay un problema con un cobro, te devolvemos ese cobro.</strong> Escríbenos y lo resolvemos.
              </span>
            </li>
          </Box>
          <Box
            component="a"
            href="https://web.flow.cl/es-cl/conocenos/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-block', mt: 1.5, fontFamily: FUENTE, fontSize: 14, fontWeight: 600, color: AU.azulHover, '&:focus-visible': { ...CONTORNO_FOCO } }}
          >
            Conoce a Flow
          </Box>
        </TarjetaAU>
      )}

      {error && <ErrorConCodigo mensaje={error} codigo={errorCodigo} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ fontFamily: FUENTE, fontSize: 14, color: AU.grisMedio, textAlign: 'center' }}>
          {automatico
            ? 'Al continuar te llevamos al sitio seguro de Flow para que registres tu tarjeta.'
            : 'Al crear la suscripción, recibirás un correo con los detalles de la suscripción y pago.'}
        </Box>
        <BotonAU cargando={enviando} onClick={enviar}>
          {automatico ? 'Registrar tarjeta y suscribirme' : 'Crear suscripción'}
        </BotonAU>
      </Box>
    </PaginaSuscripcion>
  );
};

export default SubscriptionPaymentPage;
